import { css } from "@emotion/core";
import styled from "@emotion/styled";
import { ThemeKey } from "@smartsheet/design-tokens";
import { getClientId, useIsComposingHandler } from "@smartsheet/lodestar-utils";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import React, { forwardRef, InputHTMLAttributes } from "react";
import { noop } from "../../../../constants/noop";
import { useTheme } from "../../../../theme/utils/useTheme";
import { getInputFieldHeightValue, InputFieldBaseProps } from "../../../../util/inputFieldBaseProps";
import { selectV2Theme } from "../selectV2Theme";

interface AutoSizeInputRootProps extends InputFieldBaseProps {
    themeMode: RequiredThemeKeys;
}

const sharedStyles = css`
    box-sizing: border-box;
    font-size: inherit;
    font-weight: inherit;
    font-family: inherit;
    line-height: inherit;
    font-style: normal;
    color: inherit;

    // Just need a bit more space over the what the Inline already gives us.
    padding-left: 4px;
`;

const AutoSizeInputRoot = styled("span")<AutoSizeInputRootProps>`
    ${sharedStyles}
    display: inline-block;
    width: auto;
    position: relative;
    height: ${({ themeMode, inputFieldHeight }) =>
        selectV2Theme.autoSizeInput.minHeight[getInputFieldHeightValue(themeMode, inputFieldHeight)](themeMode)};
`;

const AutoSizeInputSpacer = styled("span")`
    ${sharedStyles}
    visibility:hidden;
    display: inline;
    white-space: pre;
`;

export interface AutoSizeInputProps
    extends InputFieldBaseProps,
        Omit<InputHTMLAttributes<HTMLInputElement>, "defaultValue" | "disabled"> {
    clientId: string | undefined;
    isReadonly: boolean;
}

interface AutoSizeInputInputProps extends InputFieldBaseProps {
    isReadonly: boolean;
    themeMode: RequiredThemeKeys;
}

const AutoSizeInputInput = styled("input")<AutoSizeInputInputProps>`
    ${sharedStyles}

    box-sizing:content-box;
    position: absolute;
    top: 0;
    bottom: 0;
    // Just need a bit more space over the what the Inline already gives us.
    left: ${({ themeMode }) => selectV2Theme.autoSizeInput.left(themeMode)};
    width: 100%;

    margin: 0;
    padding: 0;

    border: none;
    outline: none;
    min-height: ${({ themeMode, inputFieldHeight }) =>
        selectV2Theme.autoSizeInput.minHeight[getInputFieldHeightValue(themeMode, inputFieldHeight)](themeMode)};
    min-width: 1px;
    background-color: transparent;

    cursor: ${({ isReadonly }) => (isReadonly ? "default" : "text")};
`;

export const AutoSizeInput = forwardRef<HTMLInputElement, AutoSizeInputProps>(
    ({ className, clientId, isReadonly, onKeyDown, onKeyUp, value, ...rest }, ref) => {
        const handleKeyDown = useIsComposingHandler(onKeyDown || noop);
        const handleKeyUp = useIsComposingHandler(onKeyUp || noop);
        const { key } = useTheme();
        const themeMode = key as ThemeKey;

        return (
            <AutoSizeInputRoot inputFieldHeight={rest.inputFieldHeight} className={className} themeMode={themeMode}>
                <AutoSizeInputSpacer>{value}</AutoSizeInputSpacer>
                <AutoSizeInputInput
                    isReadonly={isReadonly}
                    value={value}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    themeMode={themeMode}
                    {...rest}
                    data-client-id={getClientId(clientId, "-input")}
                    ref={ref}
                />
            </AutoSizeInputRoot>
        );
    }
);

AutoSizeInput.displayName = "AutoSizeInput";
